import { db, FirebaseUser } from '../../../firebase';
import {
  deleteField,
  doc,
  onSnapshot,
  updateDoc,
} from 'firebase/firestore';

import { TENANTS_COLLECTION_NAME } from './constants';
import { trackEvent } from '../server/analytics/trackEvent';
import { RoleConfig, RoleConfigMap, RoleIndexConfigMap, TenantSettings } from '../../types/tenant';
import { PositionGroup } from '../../types/position';
import { deleteRoleConfigFromSavedSearchesAndAlerts } from './scouting';
import { FormationConfig, PlatformFormation } from '../../types/formation';
import { updateTeamsWithNewFormation } from './teams';
import { getPositionsOfFormationConfig } from '../../utils/formationUtils';
import { defaultFormationConfigMap } from '../../static/constants/formations';


// get and set settings for tenant
export const getTenantSettings = (
  setTenantSettings: (tenantSettings: TenantSettings) => void,
  tenant: string,
) => {

  const tenantSettingsDocRef = doc(db, TENANTS_COLLECTION_NAME, tenant, 'settings', 'clubSettings'); // todo: should rename document to 'tenantSettings'

  const unsubscribe = onSnapshot(tenantSettingsDocRef, (doc) => {
    if (doc.exists()) {
      const tenantSettingsObject = doc.data();

      const roleConfigs: RoleConfigMap = tenantSettingsObject.roleConfigs ?? {};
      const positionGroupToRoleIds = {} as Record<PositionGroup, string[]>;

      const tempPositionGroupMap: { [key: string]: { roleId: string; orderIndex: number }[] } = {};
      Object.entries(roleConfigs).forEach(([roleId, config]) => {
        const { positionGroup, positionGroupIndex } = config;
        if (!tempPositionGroupMap[positionGroup]) tempPositionGroupMap[positionGroup] = [];
        tempPositionGroupMap[positionGroup].push({
          roleId,
          orderIndex: positionGroupIndex
        });
      });
      Object.keys(tempPositionGroupMap).forEach(positionGroup => {
        tempPositionGroupMap[positionGroup].sort((a, b) => a.orderIndex - b.orderIndex);
        positionGroupToRoleIds[positionGroup as PositionGroup] = tempPositionGroupMap[positionGroup].map(entry => entry.roleId);
      });

      const tenantSettings: TenantSettings = {
        clubId: tenantSettingsObject.clubId,
        clubName: tenantSettingsObject.clubName,
        countryCode: tenantSettingsObject.countryCode ?? 'NO',
        iterations: tenantSettingsObject.iterations ?? [],
        formation: tenantSettingsObject.formation ?? '433',
        formationConfigs: tenantSettingsObject.formationConfigs ?? {},
        roleConfigs: roleConfigs,
        positionGroupToRoleIds: positionGroupToRoleIds,
        positionToRoleIndexConfig: tenantSettingsObject.positionToRoleIndexConfig ?? {},
      };

      setTenantSettings(tenantSettings);
    }
  });

  return unsubscribe;
};


export const updateFormationConfig = async (
  formationToDelete: PlatformFormation | undefined,
  formationToAddOrUpdate: PlatformFormation | undefined,
  formationConfigToAddOrUpdate: FormationConfig | undefined,
  tenant: string,
  currentUser: FirebaseUser,
) => {

  const tenantSettingsDocRef = doc(db, TENANTS_COLLECTION_NAME, tenant, 'settings', 'clubSettings'); // todo: should rename document to 'tenantSettings'

  try {
    // if formationToDelete is equal to formationToAddOrUpdate, we are just updating the formation config
    const shouldRemoveFormation = formationToDelete !== undefined && formationToDelete !== formationToAddOrUpdate;
    if (shouldRemoveFormation) {
      await updateDoc(tenantSettingsDocRef, {
        [`formationConfigs.${formationToDelete}`]: deleteField(),
      });
    }

    const shouldAddOrUpdateFormation = formationToAddOrUpdate !== undefined && formationConfigToAddOrUpdate !== undefined;
    if (shouldAddOrUpdateFormation) {
      await updateDoc(tenantSettingsDocRef, {
        [`formationConfigs.${formationToAddOrUpdate}`]: formationConfigToAddOrUpdate
      });
    }

    const action = shouldRemoveFormation && !shouldAddOrUpdateFormation
      ? 'deleted'
      : shouldRemoveFormation && shouldAddOrUpdateFormation
        ? 'edited'
        : 'added';

    trackEvent('FormationConfigEdited', { action }, currentUser, 'user');
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'addOrUpdateFormationConfig', errorDetails: { error } }, currentUser, 'system');
  }
};


// update tenant settings formation, teams, and positionToRoleIndexConfig (no re-distribution, just cleanup of positions that are not in the new formation)
export const updateTenantFormation = async (
  newFormation: PlatformFormation,
  tenantSettings: TenantSettings,
  userEmail: string,
  tenant: string,
  currentUser: FirebaseUser,
) => {

  try {
    const newFormationConfig = tenantSettings.formationConfigs?.[newFormation] ?? defaultFormationConfigMap[newFormation];
    const currentFormation = tenantSettings.formation;
    const currentFormationConfig = tenantSettings.formationConfigs?.[currentFormation] ?? defaultFormationConfigMap[currentFormation];

    // update all teams with the new formation
    await updateTeamsWithNewFormation(
      newFormation,
      newFormationConfig,
      currentFormation,
      currentFormationConfig,
      userEmail,
      tenant,
      currentUser,
      true,
      tenantSettings.formationConfigs,
    );

    // remove roles that no longer have a position in the new formation
    const newPositions = getPositionsOfFormationConfig(newFormationConfig);
    const newPositionToRoleIndexConfig: RoleIndexConfigMap = {};
    newPositions.forEach(position => {
      if (tenantSettings.positionToRoleIndexConfig[position]) {
        newPositionToRoleIndexConfig[position] = tenantSettings.positionToRoleIndexConfig[position];
      }
    });

    // update tenant settings formation and positionToRoleIndexConfig
    const tenantSettingsDocRef = doc(db, TENANTS_COLLECTION_NAME, tenant, 'settings', 'clubSettings'); // todo: should rename document to 'tenantSettings'
    await updateDoc(tenantSettingsDocRef, {
      formation: newFormation,
      positionToRoleIndexConfig: newPositionToRoleIndexConfig,
    });

    trackEvent('TenantFormationUpdated', {}, currentUser, 'user');
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'updateTenantSettingsFormation', errorDetails: { error } }, currentUser, 'system');
  }
};


// update positionToRoleIndexConfig
export const updatePositionToRoleIndexConfig = async (
  newPositionToRoleIndexConfig: RoleIndexConfigMap,
  tenant: string,
  eventName: 'RoleConfigMoved' | 'RoleConfigAggregationRuleEdited',
  currentUser: FirebaseUser,
) => {

  // temporary tracking of potential issue
  let copyDetected = false;
  Object.values(newPositionToRoleIndexConfig).forEach(roleIndexConfig => {
    roleIndexConfig.roleIds.forEach(roleId => {
      if (roleId.endsWith('copy')) {
        copyDetected = true;
      }
    });
  });

  if (copyDetected) {
    trackEvent(
      'UnexpectedBehavior',
      { file: 'clubSettings.ts', behavior: 'Dragged role copy detected (updatePositionToRoleIndexConfig)' },
      currentUser,
      'user'
    );
  }

  const tenantSettingsDocRef = doc(db, TENANTS_COLLECTION_NAME, tenant, 'settings', 'clubSettings'); // todo: should rename document to 'tenantSettings'

  try {
    await updateDoc(tenantSettingsDocRef, {
      positionToRoleIndexConfig: newPositionToRoleIndexConfig
    });

    trackEvent(eventName, {}, currentUser, 'user');
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'updatePositionToRoleIndexConfig', errorDetails: { error } }, currentUser, 'system');
  }
};


// update the given role config
export const updateRoleConfig = async (
  roleId: string,
  newRoleConfig: RoleConfig,
  tenant: string,
  eventName: 'RoleConfigCreated' | 'RoleConfigEdited' | 'RoleConfigNameEdited',
  currentUser: FirebaseUser,
) => {

  const tenantSettingsDocRef = doc(db, TENANTS_COLLECTION_NAME, tenant, 'settings', 'clubSettings'); // todo: should rename document to 'tenantSettings'

  try {
    await updateDoc(tenantSettingsDocRef, {
      [`roleConfigs.${roleId}`]: newRoleConfig
    });

    if (eventName === 'RoleConfigCreated') {
      trackEvent(eventName, { positionGroup: newRoleConfig.positionGroup, numberOfMetrics: Object.keys(newRoleConfig.weights).length }, currentUser, 'user');
    }
    else if (eventName === 'RoleConfigEdited') {
      trackEvent(eventName, { numberOfMetrics: Object.keys(newRoleConfig.weights).length }, currentUser, 'user');
    }
    else {
      trackEvent(eventName, {}, currentUser, 'user');
    }
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'updateRoleConfig', errorDetails: { error } }, currentUser, 'system');
  }
};


// delete the given role config
// when a role config is deleted, we also need to update all savedSearches and alerts that use this role config
export const deleteRoleConfig = async (
  roleId: string,
  newPositionToRoleIndexConfig: RoleIndexConfigMap,
  tenant: string,
  currentUser: FirebaseUser,
) => {

  const tenantSettingsDocRef = doc(db, TENANTS_COLLECTION_NAME, tenant, 'settings', 'clubSettings'); // todo: should rename document to 'tenantSettings'

  try {
    await updateDoc(tenantSettingsDocRef, {
      [`roleConfigs.${roleId}`]: deleteField(),
      positionToRoleIndexConfig: newPositionToRoleIndexConfig,
    });

    trackEvent('RoleConfigDeleted', {}, currentUser, 'user');

    deleteRoleConfigFromSavedSearchesAndAlerts(roleId, tenant, currentUser);
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'deleteRoleConfig', errorDetails: { error } }, currentUser, 'system');
  }
};
